import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import {
  FileVideo,
  Clock,
  UserCheck,
  AlertTriangle,
  Trophy,
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  CalendarDays,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { cn } from "@/projects/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  // eslint-disable-next-line import/no-deprecated -- Cell still required for pie slice colors
  Cell,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { StatusBadge } from "@/projects/components/status-badge";
import { AiInsightsPanel } from "@/projects/admin/components/ai-insights-panel";
import {
  fetchAdminDashboard,
  formatDashboardDate,
  getAdminDashboardErrorMessage,
  insightValue,
  normalizeDashboardStatus,
  type AdminDashboardData,
  type DashboardPeriod,
} from "@/projects/lib/adminDashboardApi";
import { toast } from "@/projects/lib/toast";

const C = {
  royal: "oklch(0.42 0.15 265)",
  royalLight: "oklch(0.62 0.16 275)",
  gold: "oklch(0.78 0.14 85)",
  saffron: "oklch(0.72 0.18 55)",
  success: "oklch(0.62 0.16 145)",
  danger: "oklch(0.58 0.22 27)",
  info: "oklch(0.55 0.15 240)",
  muted: "oklch(0.85 0.02 265)",
};

const INSIGHT_META: Record<
  string,
  {
    icon: typeof FileVideo;
    tone: "royal" | "saffron" | "info" | "success" | "danger" | "gold";
    href: "/admin/submissions" | "/admin/teachers";
  }
> = {
  total_submissions: {
    icon: FileVideo,
    tone: "royal",
    href: "/admin/submissions",
  },
  pending_review: { icon: Clock, tone: "saffron", href: "/admin/submissions" },
  assigned: { icon: UserCheck, tone: "info", href: "/admin/submissions" },
  ready_for_reward: {
    icon: Trophy,
    tone: "success",
    href: "/admin/submissions",
  },
  not_proper: {
    icon: AlertTriangle,
    tone: "danger",
    href: "/admin/submissions",
  },
  active_teachers: { icon: Users, tone: "gold", href: "/admin/teachers" },
};

const STATUS_COLORS: Record<string, string> = {
  pending: C.saffron,
  assigned: C.royal,
  ready_for_reward: C.success,
  not_proper: C.danger,
};

const DEFAULT_PERIODS: { key: DashboardPeriod; label: string }[] = [
  { key: "last_7_days", label: "Last 7 days" },
  { key: "last_30_days", label: "Last 30 days" },
  { key: "all_time", label: "All time" },
];

export function AdminDashboard() {
  const [period, setPeriod] = useState<DashboardPeriod>("last_30_days");
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const dashboard = await fetchAdminDashboard(period);
        if (!cancelled) {
          setData(dashboard);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            getAdminDashboardErrorMessage(
              error,
              "Failed to load admin dashboard."
            )
          );
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const periodFilters = data?.period_filters?.length
    ? data.period_filters
    : DEFAULT_PERIODS.map(p => ({
        key: p.key,
        label: p.label,
        selected: p.key === period,
      }));

  const kpis = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.live_insights.map(insight => {
      const meta = INSIGHT_META[insight.key] ?? {
        icon: FileVideo,
        tone: "royal" as const,
        href: "/admin/submissions" as const,
      };
      return {
        key: insight.key,
        label: insight.label,
        value: insight.value,
        delta: insight.trend_percent,
        up: insight.trend_direction !== "down",
        icon: meta.icon,
        tone: meta.tone,
        href: meta.href,
        spark: (insight.sparkline ?? []).map(v => ({ v })),
      };
    });
  }, [data]);

  const trendSeries = useMemo(
    () =>
      (data?.submission_trend.series ?? []).map(point => ({
        date: formatDashboardDate(point.date),
        submissions: point.submissions,
        verified: point.verified,
      })),
    [data]
  );

  const momentumSeries = useMemo(
    () =>
      (data?.momentum.series ?? []).map(point => ({
        date: formatDashboardDate(point.date),
        submissions: point.submissions,
        verified: point.verified,
      })),
    [data]
  );

  const byStatus = useMemo(
    () =>
      (data?.status_distribution.items ?? []).map(item => ({
        name: item.label,
        value: item.count,
        color: STATUS_COLORS[item.status] ?? C.royal,
      })),
    [data]
  );

  const byTeacher = useMemo(
    () =>
      (data?.teacher_performance.items ?? []).map(t => ({
        name: t.name || t.full_name?.split(" ")[0] || t.teacher_id,
        reward: t.ready_for_reward,
        notProper: t.not_proper,
        pending: t.pending,
      })),
    [data]
  );

  const topTeachers = useMemo(
    () =>
      (data?.top_verifiers.items ?? []).map(t => ({
        name: t.name || t.full_name?.split(" ")[0] || t.teacher_id,
        verified: t.processed,
      })),
    [data]
  );

  const rewardRate = data?.reward_rate.percent ?? 0;
  const verificationRate = data?.reward_rate.verification_rate_percent ?? 0;
  const ofSubmissions = data?.reward_rate.of_submissions ?? 0;
  const gauge = [{ name: "rate", value: rewardRate, fill: C.gold }];
  const recent = data?.recent_submissions.items ?? [];

  const aiCounts = {
    total: insightValue(data?.live_insights ?? [], "total_submissions"),
    pending: insightValue(data?.live_insights ?? [], "pending_review"),
    assigned: insightValue(data?.live_insights ?? [], "assigned"),
    reward: insightValue(data?.live_insights ?? [], "ready_for_reward"),
    notProper: insightValue(data?.live_insights ?? [], "not_proper"),
    teachers: insightValue(data?.live_insights ?? [], "active_teachers"),
  };

  return (
    <DashboardLayout
      role="admin"
      title="Executive Dashboard"
      breadcrumb={["Home", "Dashboard"]}
    >
      {/* Filter ribbon */}
      <div className="from-royal-soft via-card to-gold-soft/40 shadow-soft mb-4 flex flex-wrap items-center gap-3 rounded-xl border bg-gradient-to-r p-3">
        <div className="text-royal flex items-center gap-2 text-sm font-semibold">
          <Activity className="h-4 w-4" /> Live Insights
        </div>
        <div className="flex-1" />
        <div className="bg-background flex items-center gap-1 rounded-lg border p-1 text-xs">
          <CalendarDays className="text-muted-foreground ml-2 h-3.5 w-3.5" />
          {periodFilters.map(filter => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setPeriod(filter.key as DashboardPeriod)}
              disabled={loading}
              className={cn(
                "rounded-md px-3 py-1.5 font-medium transition-colors",
                period === filter.key
                  ? "bg-royal shadow-soft text-white"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-24 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading dashboard…
        </div>
      ) : !data ? (
        <div className="text-muted-foreground py-24 text-center text-sm">
          Unable to load dashboard data.
        </div>
      ) : (
        <div className={cn(loading && "pointer-events-none opacity-60")}>
          {/* KPI tiles with sparklines */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {kpis.map(k => (
              <Link
                key={k.key}
                to={k.href}
                className="group bg-card shadow-soft hover:shadow-card relative overflow-hidden rounded-xl border p-4 transition-all hover:-translate-y-0.5"
              >
                <div
                  className={cn(
                    "absolute inset-x-0 top-0 h-1",
                    k.tone === "royal" && "bg-royal",
                    k.tone === "saffron" && "bg-saffron",
                    k.tone === "gold" && "bg-gold",
                    k.tone === "success" && "bg-success",
                    k.tone === "danger" && "bg-destructive",
                    k.tone === "info" && "bg-info"
                  )}
                />
                <div className="flex items-start justify-between">
                  <div
                    className={cn(
                      "grid h-9 w-9 place-items-center rounded-lg",
                      k.tone === "royal" && "bg-royal-soft text-royal",
                      k.tone === "saffron" && "bg-saffron-soft text-saffron",
                      k.tone === "gold" && "bg-gold-soft text-gold",
                      k.tone === "success" && "bg-success/10 text-success",
                      k.tone === "danger" &&
                        "bg-destructive/10 text-destructive",
                      k.tone === "info" && "bg-royal-soft text-info"
                    )}
                  >
                    <k.icon className="h-4.5 w-4.5" />
                  </div>
                  <div
                    className={cn(
                      "flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                      k.up
                        ? "bg-success/10 text-success"
                        : "bg-destructive/10 text-destructive"
                    )}
                  >
                    {k.up ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {k.delta}%
                  </div>
                </div>
                <div className="mt-3 text-2xl font-bold tracking-tight">
                  {k.value}
                </div>
                <div className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                  {k.label}
                </div>
                {k.spark.length > 0 && (
                  <div className="mt-2 h-8">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={k.spark}>
                        <defs>
                          <linearGradient
                            id={`sp-${k.key}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={C.royal}
                              stopOpacity={0.4}
                            />
                            <stop
                              offset="100%"
                              stopColor={C.royal}
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey="v"
                          stroke={C.royal}
                          strokeWidth={1.5}
                          fill={`url(#sp-${k.key})`}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* AI Insights */}
          <div className="mt-4">
            <AiInsightsPanel
              range={data.period_label}
              total={aiCounts.total}
              pending={aiCounts.pending}
              assigned={aiCounts.assigned}
              reward={aiCounts.reward}
              notProper={aiCounts.notProper}
              teachers={aiCounts.teachers}
              rewardRate={rewardRate}
              verificationRate={verificationRate}
              topTeachers={topTeachers}
            />
          </div>

          {/* Main chart grid */}
          <div className="mt-4 grid gap-4 lg:grid-cols-6">
            <Panel
              title={data.submission_trend.title}
              subtitle={data.submission_trend.subtitle}
              className="lg:col-span-4"
            >
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={trendSeries}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="g-subs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.royal} stopOpacity={0.5} />
                      <stop
                        offset="100%"
                        stopColor={C.royal}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                    <linearGradient id="g-ver" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.gold} stopOpacity={0.5} />
                      <stop
                        offset="100%"
                        stopColor={C.gold}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      fontSize: 12,
                      border: "1px solid oklch(0.92 0.02 260)",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                    iconType="circle"
                  />
                  <Area
                    type="monotone"
                    dataKey="submissions"
                    name="Submissions"
                    stroke={C.royal}
                    strokeWidth={2}
                    fill="url(#g-subs)"
                  />
                  <Area
                    type="monotone"
                    dataKey="verified"
                    name="Verified"
                    stroke={C.gold}
                    strokeWidth={2}
                    fill="url(#g-ver)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <Panel
              title={data.reward_rate.title}
              subtitle={data.reward_rate.subtitle}
              className="lg:col-span-2"
            >
              <div className="relative">
                <ResponsiveContainer width="100%" height={230}>
                  <RadialBarChart
                    innerRadius="70%"
                    outerRadius="100%"
                    data={gauge}
                    startAngle={220}
                    endAngle={-40}
                  >
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 100]}
                      tick={false}
                    />
                    <RadialBar
                      dataKey="value"
                      cornerRadius={12}
                      background={{ fill: "oklch(0.94 0.02 265)" }}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-royal text-4xl font-extrabold">
                    {rewardRate}%
                  </div>
                  <div className="text-muted-foreground text-xs">
                    of {ofSubmissions} submissions
                  </div>
                </div>
              </div>
              <div className="bg-muted/40 mt-2 flex items-center justify-between rounded-lg px-3 py-2 text-xs">
                <span className="text-muted-foreground">Verification rate</span>
                <span className="text-royal font-semibold">
                  {verificationRate}%
                </span>
              </div>
            </Panel>

            <Panel
              title={data.status_distribution.title}
              subtitle={data.status_distribution.subtitle}
              className="lg:col-span-2"
            >
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={byStatus}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {byStatus.map(s => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {byStatus.map(s => (
                  <div key={s.name} className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: s.color }}
                    />
                    <span className="text-muted-foreground truncate">
                      {s.name}
                    </span>
                    <span className="ml-auto font-semibold">{s.value}</span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel
              title={data.teacher_performance.title}
              subtitle={data.teacher_performance.subtitle}
              className="lg:col-span-4"
            >
              {byTeacher.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                  No teacher performance data.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={byTeacher}
                    margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                    <XAxis
                      dataKey="name"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                    <Bar
                      dataKey="reward"
                      name="Ready for Reward"
                      stackId="a"
                      fill={C.success}
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="notProper"
                      name="Not Proper"
                      stackId="a"
                      fill={C.danger}
                    />
                    <Bar
                      dataKey="pending"
                      name="Pending"
                      stackId="a"
                      fill={C.royal}
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            <Panel
              title={data.top_verifiers.title}
              subtitle={data.top_verifiers.subtitle}
              className="lg:col-span-3"
            >
              {topTeachers.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                  No verifiers yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={topTeachers}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      opacity={0.2}
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      width={70}
                    />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar
                      dataKey="verified"
                      fill={C.royal}
                      radius={[0, 6, 6, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            <Panel
              title={data.momentum.title}
              subtitle={data.momentum.subtitle}
              className="lg:col-span-3"
            >
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={momentumSeries}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="submissions"
                    name="Submissions"
                    stroke={C.royal}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: C.royal }}
                  />
                  <Line
                    type="monotone"
                    dataKey="verified"
                    name="Verified"
                    stroke={C.gold}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: C.gold }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          {/* Recent activity feed */}
          <Panel
            title={data.recent_submissions.title}
            subtitle={data.recent_submissions.subtitle}
            className="mt-4"
          >
            {recent.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                No submissions in range.
              </div>
            ) : (
              <div className="divide-y">
                {recent.map(s => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5">
                    <div className="bg-royal-soft text-royal grid h-9 w-9 place-items-center rounded-lg">
                      <FileVideo className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {s.title}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {s.student_name} ·{" "}
                        <span className="font-mono">{s.reference}</span> ·{" "}
                        {new Date(s.submitted_at).toLocaleString()}
                      </div>
                    </div>
                    <StatusBadge status={normalizeDashboardStatus(s.status)} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Link
                to="/admin/submissions"
                className="text-royal inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                View all submissions <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </Panel>
        </div>
      )}
    </DashboardLayout>
  );
}

function Panel({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "bg-card shadow-soft rounded-xl border p-4 sm:p-5",
        className
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && (
            <div className="text-muted-foreground text-[11px]">{subtitle}</div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
